
import React, { useState } from "react";
import { CloseIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    Text,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Switch,
    VStack,
    useColorModeValue,
    useToast,
    IconButton,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    useDisclosure,
    Center,
    Alert,
    AlertIcon
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { emailRepository } from 'api/EmailRepository';
import { useNavigate } from "react-router-dom";
type AlertStatus = "success" | "error" | "info" | "warning" | "loading";

export default function AddEmail() {
    const [name, setName] = useState("");
    const [channel, setChannel] = useState("");
    const [notificationType, setNotificationType] = useState("");
    const [subType, setSubType] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const textColor = useColorModeValue("gray.700", "white");
    const cardBg = useColorModeValue("white", "navy.800");

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = React.useRef<HTMLButtonElement | null>(null);
    const [centerAlertVisible, setCenterAlertVisible] = useState(false);
    const [centerAlertStatus, setCenterAlertStatus] = useState<AlertStatus>("info");
    const [centerAlertMessage, setCenterAlertMessage] = useState("");

    const showCenteredAlert = (status: AlertStatus, message: string) => {
        setCenterAlertStatus(status);
        setCenterAlertMessage(message);
        setCenterAlertVisible(true);
        setTimeout(() => setCenterAlertVisible(false), 2000);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await emailRepository.addEmail({ name, channel, notificationType, subType, subject, body, isActive });
            showCenteredAlert("success", " Template Added Successfully!");
            setTimeout(() => navigate("/admin/Template/TemplateList"), 2500);
        } catch (err) {
            showCenteredAlert("error", "Failed to add template");
        } finally {
            setLoading(false);
        }
    };

    const handleClearForm = () => {
        setName("");
        setChannel("");
        setNotificationType("");
        setSubType("");
        setSubject("");
        setBody("");
        setIsActive(true);
        showCenteredAlert("info", "All fields have been reset.");
        onClose();
    };

    return (
        <Flex
            direction="column"
            minH="100vh"
            overflow="hidden"
            align="center"
            justify="flex-start"
            position="relative"
            px={{ base: '20px', md: '40px' }}
            pb="60px"
        >
            <Box
                position="absolute"
                top="90"
                w="100%"
                h="40vh"
                bgGradient="linear(to-b, brand.400, brand.600)"
                borderRadius="20px"
            />
            <Card
                w={{ sm: '330px', md: '700px', lg: '850px' }}
                p="30px"
                mt={{ md: '150px' }}
                mx="auto"
                zIndex={1}
                overflowY="auto"
                borderRadius="16px"
            >
                <Flex justify="space-between" align="center" mb="20px">
                    <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                        Add
                    </Text>
                    <IconButton
                        aria-label="Close"
                        icon={<CloseIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/Template/TemplateList')}
                    />
                </Flex>
                <form onSubmit={handleSubmit}>
                    <VStack spacing="20px" align="stretch">
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="1px" color={textColor} fontSize="sm"> Name</FormLabel>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter name"
                                h="44px"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="1px" color={textColor} fontSize="sm">Channel</FormLabel>
                            <Input
                                value={channel}
                                onChange={(e) => setChannel(e.target.value)}
                                placeholder="Enter channel (e.g., Email, SMS)"
                                h="44px"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="1px" color={textColor} fontSize="sm">Notification</FormLabel>
                            <Input
                                value={notificationType}
                                onChange={(e) => setNotificationType(e.target.value)}
                                placeholder="Enter notification"
                                h="44px"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="1px" color={textColor} fontSize="sm">Subtype</FormLabel>
                            <Input
                                value={subType}
                                onChange={(e) => setSubType(e.target.value)}
                                placeholder="Enter subtype"
                                h="44px"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="1px" color={textColor} fontSize="sm">Subject</FormLabel>
                            <Input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Enter subject"
                                h="44px"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold" mb="8px" color={textColor} fontSize="sm">Body</FormLabel>
                            <Textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Enter body content"
                                rows={6}
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0" fontWeight="bold" fontSize="sm">
                                Active
                            </FormLabel>
                            <Switch
                                colorScheme="blue"
                                isChecked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                        </FormControl>

                        <Flex justify="center" gap="20px" mt="24px">
                            <Button
                                variant="outline"
                                fontSize="sm"
                                borderRadius="16px"
                                w={{ base: '128px', md: '148px' }}
                                h="46px"
                                onClick={onOpen}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="darkBrand"
                                colorScheme="blue"
                                fontSize="sm"
                                borderRadius="16px"
                                w={{ base: '128px', md: '148px' }}
                                h="46px"
                                type="submit"
                                isLoading={loading}
                                loadingText="Saving..."
                            >
                                Submit
                            </Button>
                        </Flex>
                    </VStack>
                </form>
            </Card>

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Clear Form
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to clear all fields? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleClearForm} ml={3}>
                                Clear
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {centerAlertVisible && (
                <Center
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex={60}
                    pointerEvents="none"
                >
                    <Alert
                        status={centerAlertStatus}
                        variant="solid"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        width="600px"
                        borderRadius="14px"
                        boxShadow="xl"
                        py={10}
                        px={16}
                        pointerEvents="auto"
                    >
                        <AlertIcon boxSize="40px" mr={0} />

                        <Text fontSize="lg" fontWeight="700" mt={1}>
                            {centerAlertMessage}
                        </Text>
                    </Alert>
                </Center>
            )}
        </Flex>
    );
}